'use client'

import { useEffect, useState } from 'react'
import { Check, AlertCircle, Loader2, Download } from 'lucide-react'
import type { Toast } from '@/lib/toast'

/**
 * Container global de toasts (en bas-droite, au-dessus du lecteur).
 * Écoute les événements `blc-toast` (add / update / remove).
 */
export default function ToastHost() {
  const [toasts, setToasts] = useState<Toast[]>([])

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent<any>).detail
      if (!detail) return

      if (detail.action === 'add') {
        const t: Toast = detail.toast
        setToasts(list => [...list, t])
        if (t.duration !== -1) {
          setTimeout(() => {
            setToasts(list => list.filter(x => x.id !== t.id))
          }, t.duration ?? 4000)
        }
      } else if (detail.action === 'update') {
        setToasts(list => list.map(x => x.id === detail.id ? { ...x, ...detail.patch } : x))
      } else if (detail.action === 'remove') {
        setToasts(list => list.filter(x => x.id !== detail.id))
      }
    }
    window.addEventListener('blc-toast', onEvent)
    return () => window.removeEventListener('blc-toast', onEvent)
  }, [])

  if (toasts.length === 0) return null

  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        // Au-dessus du lecteur du bas : sa hauteur réelle est publiée par la
        // PlayerBar dans --blc-playerbar-h (repli 96 px hors de l'app).
        bottom: 'calc(var(--player-bottom, 0px) + var(--blc-playerbar-h, 96px) + var(--toast-extra-bottom, 16px))',
        zIndex: 70,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 10,
        maxWidth: 'min(380px, calc(100vw - 32px))',
        pointerEvents: 'none',
      }}
    >
      {toasts.map(t => <ToastCard key={t.id} t={t} />)}
    </div>
  )
}

function ToastCard({ t }: { t: Toast }) {
  const isProgress = t.kind === 'progress'
  const pct = Math.max(0, Math.min(1, t.progress ?? 0))

  const icon = t.kind === 'success' ? <Check size={15} className="text-emerald-400" />
    : t.kind === 'error'   ? <AlertCircle size={15} className="text-rose-300" />
    : isProgress           ? <Loader2 size={15} className="animate-spin text-a4" />
    : <Download size={15} className="text-a4" />

  return (
    <div
      className="rounded-2xl border border-white/[0.08] overflow-hidden shadow-[0_14px_36px_rgba(0,0,0,.5),0_0_24px_rgb(var(--a2)/.18)]"
      style={{
        pointerEvents: 'auto', minWidth: 260,
        background: 'linear-gradient(135deg, rgba(26,22,66,0.96), rgba(17,19,46,0.96))',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        animation: 'blcToastIn 220ms cubic-bezier(0.22,0.8,0.24,1)',
      }}
    >
      <style>{`@keyframes blcToastIn{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:none}}`}</style>
      <div className="flex items-center gap-3 p-2.5 pr-3.5">
        {t.thumbnail
          ? <img src={t.thumbnail} alt="" className="w-10 h-10 rounded-xl object-cover shrink-0" />
          : <div className="w-10 h-10 rounded-xl bg-a2/15 flex items-center justify-center shrink-0">
              {icon}
            </div>}
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-white truncate flex items-center gap-1.5">
            {t.thumbnail && icon}
            <span className="truncate">{t.msg}</span>
          </div>
          {isProgress && (
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-a1 to-a3 transition-[width] duration-200"
                  style={{ width: `${pct * 100}%` }}
                />
              </div>
              <span className="text-[11px] text-white/65 tabular-nums shrink-0">
                {Math.round(pct * 100)}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
